import React, { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Check, MapPin, ArrowUpRight } from "lucide-react";
import Reveal from "../components/site/Reveal";
import { CtaLink } from "../components/site/Common";
import { OrganicDot } from "../components/site/Logo";
import { FinalCta } from "../components/site/HomeSections";
import { SVC_ICONS } from "../components/site/ServiceBlocks";
import { SERVICE_PAGES, SERVICE_HUB } from "../seo";
import { AREAS } from "../cities";
import { CITIES, slugify } from "../mock";

export default function ServiceHub() {
  const { service } = useParams();
  const svc = SERVICE_PAGES.find((s) => s.slug === service);
  const groups = useMemo(() => Object.values(AREAS).map((a) => [a, CITIES.filter((c) => c.area === a).sort((x, y) => x.name.localeCompare(y.name, "fr"))]).filter(([, l]) => l.length), []);
  if (!svc) return <Navigate to="/" replace />;
  const I = SVC_ICONS[svc.icon];
  return (
    <div data-testid="service-hub">
      <section className="relative overflow-hidden bg-gradient-to-b from-[#f3ede1] to-[#faf7f0]">
        <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#d9895a]/15 blur-3xl" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 lg:pt-16 pb-16 grid lg:grid-cols-12 gap-10 items-start relative">
          <Reveal className="lg:col-span-7">
            <nav className="text-sm font-semibold text-[#6d7268]"><Link to="/" className="hover:text-[#13261c]">Accueil</Link> <span className="mx-1.5">/</span><span className="text-[#13261c]">{svc.name}</span></nav>
            <h1 className="mt-6 text-[#13261c]" data-testid="hub-title">
              <span className="block font-logo font-black tracking-[-0.03em] text-[40px] sm:text-[56px] lg:text-[64px] leading-[1]">{svc.name}<OrganicDot className="inline-block align-baseline ml-1 w-3 h-3" /></span>
              <span className="block font-script text-[30px] sm:text-[40px] text-[#d9895a] leading-[1.2] mt-2">de la Côte d'Émeraude à la baie</span>
            </h1>
            <p className="mt-5 text-lg text-[#4a5249] leading-relaxed max-w-2xl" data-testid="hub-intro">{SERVICE_HUB[svc.slug]}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink to="/rendez-vous" testid="hub-rdv">Prendre rendez-vous</CtaLink>
              <CtaLink to="/tarifs" variant="outline" testid="hub-tarifs">Voir les formules</CtaLink>
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-5 rounded-[32px] bg-[#13261c] text-[#faf7f0] p-8 grain relative overflow-hidden">
            <span className="w-12 h-12 rounded-2xl bg-[#d9895a] grid place-items-center"><I className="w-6 h-6 text-[#13261c]" /></span>
            <ul className="mt-6 space-y-3">{svc.points.map((t) => <li key={t} className="flex gap-3"><Check className="w-4 h-4 mt-1 shrink-0 text-[#d9895a]" />{t}</li>)}</ul>
            <p className="mt-5 text-sm text-[#faf7f0]/70">{svc.plans}</p>
          </Reveal>
        </div>
      </section>
      <section className="pb-20" data-testid="hub-cities">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-10">
          <h2 className="font-logo font-black tracking-[-0.025em] text-3xl lg:text-4xl text-[#13261c]">{svc.name.toLowerCase().replace("airbnb", "Airbnb")} dans votre commune</h2>
          {groups.map(([area, list]) => (
            <div key={area}>
              <p className="font-bold text-[#c4703f] uppercase tracking-[0.14em] text-sm">{area}</p>
              <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                {list.map((c) => (
                  <Link key={c.name} to={`/villes/${slugify(c.name)}/${svc.slug}`} data-testid={`hub-city-${slugify(c.name)}`} className="group flex items-center justify-between rounded-2xl p-4 ring-1 ring-[#e6dcc8] bg-white hover:bg-[#13261c] hover:text-white text-[#13261c] transition-colors">
                    <span className="flex items-center gap-2 font-bold text-sm"><MapPin className="w-4 h-4 text-[#c4703f] group-hover:text-[#d9895a]" />{svc.name} {c.name}</span>
                    <ArrowUpRight className="w-4 h-4 opacity-40 group-hover:opacity-100" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <FinalCta />
    </div>
  );
}
